import "@/styles/globals.css";
import "@/styles/AnimatedCarousel.scss";

import { Box, Center, Spinner, Text } from "@chakra-ui/react";
import { APIProvider } from "@vis.gl/react-google-maps";
import Script from "next/script";
import { SessionProvider, useSession } from "next-auth/react";
import { useStore } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";

import SessionRefresher from "@/components/auth/SessionRefresher";
import DisclaimerDialog from "@/components/general/DisclaimerDialog";
import { Provider } from "@/components/ui/provider";
import { Toaster } from "@/components/ui/toaster";
import { GOOGLE_MAPS_LIBRARIES, googleMapsApiKey } from "@/config/config";
import { DisclaimerProvider, useDisclaimer } from "@/context/disclaimerContext";
import { wrapper } from "@/hooks";
import Unauthorized from "@/sections/unauthorized/unauthorized";
import { poppins } from "@/styles/fonts";

function App({ Component, pageProps: { session, ...pageProps } }) {
    // Maps loading: APIProvider below is the app's ONLY Maps loader; consumers
    // gate themselves on useGoogleMapsLoaded() (vis.gl-backed). The old
    // app-wide isLoaded gate rendered null during SSR and shipped an empty
    // page body to every non-JS client (crawlers, social scrapers, LLM bots).

    const store = useStore((state) => state);

    const app = (
        <SessionProvider
            session={session}
            // Re-fetch session every 15 minutes
            refetchInterval={15 * 60}
            // Re-fetches session when window is focused
            refetchOnWindowFocus={true}
        >
            <Provider forcedTheme="light">
                <style jsx global>{`
                    :root {
                        font-family: ${poppins.style.fontFamily};
                    }
                    body {
                        font-family: ${poppins.style.fontFamily};
                    }
                `}</style>
                <APIProvider apiKey={googleMapsApiKey} libraries={GOOGLE_MAPS_LIBRARIES}>
                    <DisclaimerProvider>
                        <Auth>
                            <SessionRefresher />
                            <Component {...pageProps} />
                            <Toaster />
                            <GlobalDisclaimerDialog />
                        </Auth>
                    </DisclaimerProvider>
                </APIProvider>
            </Provider>
        </SessionProvider>
    );

    // redux-persist never bootstraps during SSR, so PersistGate with
    // loading={null} would blank the server HTML. Passing the SAME tree as
    // both `loading` and `children` keeps the SSR output, the client's first
    // (hydration) render, and the post-rehydration render identical. An
    // asymmetric server/client branch here breaks hydration at the root:
    // React orphans the server DOM and appends a duplicate, dead page.
    // Components briefly see pre-rehydration store state; that's the
    // standard trade for having SSR content at all.
    return (
        <>
            <PersistGate loading={app} persistor={store.__persistor}>
                {app}
            </PersistGate>
            <AnalyticsScripts />
        </>
    );
}

// Analytics/tracking scripts. These previously lived inside _document's
// <Head> as next/script afterInteractive — an unsupported placement that
// desyncs the head manager's element accounting and crashes the app
// ("Cannot read properties of null (reading 'content')") the moment a
// page-level <Head> updates. _app is the documented home for them.
// NOTE: four GA4 properties + GTM + Pixel + Hotjar is known tag chaos —
// consolidation is tracked separately; this move is placement-only.
function AnalyticsScripts() {
    return (
        <>
            <Script
                src="https://www.googletagmanager.com/gtag/js?id=G-0XDQJS08EV"
                strategy="afterInteractive"
            />
            <Script
                src="https://www.googletagmanager.com/gtag/js?id=G-9QCSVSVXNP"
                strategy="afterInteractive"
            />
            <Script
                src="https://www.googletagmanager.com/gtag/js?id=G-SG1C1X83XT"
                strategy="afterInteractive"
            />
            <Script
                id="ga-config"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-9QCSVSVXNP');
              gtag('config', 'G-0XDQJS08EV');
              gtag('config', 'G-SG1C1X83XT');
            `,
                }}
            />
            <Script
                id="facebook-pixel"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window,document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '910168250117925');
              fbq('track', 'PageView');
            `,
                }}
            />
            <Script
                id="hotjar"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
              (function(h,o,t,j,a,r){
                h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                h._hjSettings={hjid:2988646,hjsv:6};
                a=o.getElementsByTagName('head')[0];
                r=o.createElement('script');r.async=1;
                r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                a.appendChild(r);
              })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
            `,
                }}
            />
        </>
    );
}

function GlobalDisclaimerDialog() {
    const { showDisclaimer, acceptDisclaimer, hideDisclaimerDialog } = useDisclaimer();

    return (
        <DisclaimerDialog
            isOpen={showDisclaimer}
            onClose={hideDisclaimerDialog}
            onAccept={acceptDisclaimer}
        />
    );
}

function Auth({ children }) {
    // TODO: Handle all this logic in a middleware when migrating to App Router
    const { data: session, status } = useSession();

    const environment = process.env.NEXT_PUBLIC_ENV || "develop";
    const isProduction = environment == "production";

    // Production is public: never block rendering on the session fetch — the
    // old loading gate ran first and replaced server-rendered HTML with a
    // spinner on every page that doesn't provide a session via SSR.
    if (isProduction) {
        return children;
    }

    // Non-production stays gated to the tech role; keep the loading state to
    // avoid flashing the unauthorized page while the session is fetched.
    if (status === "loading") {
        return (
            <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
                <Center>
                    <Spinner size="lg" color="blue.500" />
                    <Text ml={3}>Cargando...</Text>
                </Center>
            </Box>
        );
    }

    if (session?.roles?.includes("tech")) {
        return children;
    } else {
        return <Unauthorized />;
    }
}

export default wrapper.withRedux(App);
